import { useTranslation } from 'react-i18next';
import type { Subnet } from '@scio3/shared-types';
import { TableCell, TableRow } from '@scio3/ui';
import { cn } from '@/lib/utils';

interface Props {
  subnet: Subnet;
}

/**
 * Single row in the Subnets table.
 * Renders CIDR, type badge, provisioner flag, and a usage bar
 * (used / total). When `total` is null (infinite), shows "N / ∞".
 */
export function SubnetRow({ subnet }: Props) {
  const { t } = useTranslation();
  const hasFiniteTotal = typeof subnet.total === 'number' && subnet.total > 0;
  const pct =
    hasFiniteTotal && subnet.total ? Math.min(100, (subnet.used / subnet.total) * 100) : 0;

  return (
    <TableRow data-testid={`subnet-row-${subnet.name}`}>
      <TableCell className="font-medium">{subnet.name}</TableCell>
      <TableCell className="font-mono text-xs">{subnet.cidr}</TableCell>
      <TableCell>
        <span
          className={cn(
            'rounded-sm px-2 py-0.5 text-xs font-medium',
            subnet.type === 'person'
              ? 'bg-secondary text-secondary-foreground'
              : 'bg-accent text-accent-foreground',
          )}
        >
          {subnet.type}
        </span>
      </TableCell>
      <TableCell>
        {subnet.allowProvisioner ? (
          <span className="text-success">{t('Yes')}</span>
        ) : (
          <span className="text-muted-foreground">{t('No')}</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-[width]"
                style={{ width: `${pct}%` }}
                aria-label={t('Usage')}
              />
            </div>
          </div>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {subnet.used.toLocaleString()} / {hasFiniteTotal ? subnet.total?.toLocaleString() : '∞'}
          </span>
        </div>
      </TableCell>
    </TableRow>
  );
}
